import { useMemo } from 'react';

import type {
  DashboardResultOverview,
  ResponseRateData,
  UpstreamApiDashboardData,
} from 'src/types/dashboard';
import type { SurveyListItem } from 'src/types/survey';
import useResponseRateQuery from 'src/components/dashboard/hooks/useResponseRateQuery';
import type { AggregateList } from 'src/components/followUp/common/types';

import useStatisticsData, { statisticsWidgets } from './useStatisticsData';
import useWidgetData, { pathMap } from './useWidgetData';

const useWidgetTileData = (
  survey: SurveyListItem,
  dashboardResults?: DashboardResultOverview,
) => {
  const widgetType = dashboardResults?.widget?.widget_type;

  const {
    statisticsData,
    isFetchingStatistics,
    errorMsgStatistics,
    statisticsDataline,
  } = useStatisticsData(dashboardResults);

  const {
    data: widgetData,
    isFetching: widgetDataIsFetching,
    errorMsg: widgetDataErrorMsg,
    comparisonData: widgetDataComparisonData,
  } = useWidgetData(survey, dashboardResults);

  const isFetching = isFetchingStatistics && widgetDataIsFetching;
  const hasError = !!(errorMsgStatistics || widgetDataErrorMsg);

  const { data: responseRate } = useResponseRateQuery(
    survey.id,
    !!dashboardResults && !statisticsDataline && !isFetching && !hasError,
  );

  const responseRateData: ResponseRateData | undefined = useMemo(() => {
    if (statisticsDataline) {
      return {
        responseRate: statisticsDataline?.response_rate ?? null,
        invited: statisticsDataline?.headcount ?? null,
        respondents: statisticsDataline?.returns ?? null,
      };
    }
    if (responseRate) {
      return responseRate;
    }
    return undefined;
  }, [statisticsDataline, responseRate]);

  const queryResults: {
    data?: AggregateList | UpstreamApiDashboardData;
    isFetching: boolean;
    errorMsg: string;
  } = useMemo(() => {
    const emptyResult = { data: undefined, isFetching: false, errorMsg: '' };
    if (typeof widgetType !== 'number') return emptyResult;

    if (statisticsWidgets.includes(widgetType))
      return {
        data: statisticsData,
        isFetching: isFetchingStatistics,
        errorMsg: errorMsgStatistics,
      };

    if (Object.keys(pathMap).includes(String(widgetType))) {
      return {
        data: widgetData,
        isFetching: widgetDataIsFetching,
        errorMsg: widgetDataErrorMsg,
      };
    }

    return emptyResult;
  }, [
    widgetType,
    statisticsData,
    isFetchingStatistics,
    errorMsgStatistics,
    widgetData,
    widgetDataIsFetching,
    widgetDataErrorMsg,
  ]);

  return {
    ...queryResults,
    comparisonData: widgetDataComparisonData,
    responseRateData,
  };
};

export default useWidgetTileData;
